'use client';

import {
  Autocomplete,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AzureVnetConnectivityConfig } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { BottomDialog } from '@/components/dialogs/bottomDialog';
import { PanelFooter } from '@/components/panels/panelFooter';
import { PanelHeader } from '@/components/panels/panelHeader';
import { useDAL } from '@/contexts/useDAL';

export const AzureCustomizeNetworkBottomDialog = ({
  accountId,
  config: initialConfig,
  onSave,
  onCancel,
}: {
  accountId: string;
  config: AzureVnetConnectivityConfig | undefined;
  onSave: (config: AzureVnetConnectivityConfig) => void;
  onCancel: () => void;
}) => {
  const dal = useDAL();
  const [subnetNames, setSubnetNames] = useState(
    initialConfig?.subnetNames ?? []
  );

  const { data: subnets } = dal.restore.azureRestoreConfig.useListSubnets(
    accountId,
    initialConfig?.resourceGroup || '',
    initialConfig?.vnetName || ''
  );

  const subnetOptions = (
    subnets?.subnets.map((subnet) => subnet.name) ?? []
  ).filter((name): name is string => !!name);

  const onSaveClick = () => {
    if (initialConfig) {
      onSave({
        ...initialConfig,
        subnetNames,
      });
    }
  };

  return (
    <BottomDialog open={!!initialConfig}>
      <Stack className='h-full'>
        <PanelHeader
          onClose={onCancel}
          title='Customize VNet'
          subtitle={
            <Stack className='mx-[84px] my-[24px]' gap='24px' flexGrow={1}>
              <Stack direction='row' gap='4px'>
                <Typography component='span' fontWeight={300}>
                  Customizing settings for
                </Typography>
                <Typography component='span' className='font-semibold'>
                  {`${initialConfig?.vnetName} (${initialConfig?.region}).`}
                </Typography>
              </Stack>
              <Typography className='w-[700px]' fontWeight={300}>
                Choose subnets within your VNet for restore nodes. The first
                subnet is used as primary; additional subnets serve as
                fallbacks.
              </Typography>
            </Stack>
          }
        />
        <Stack className='mx-[84px] my-[24px]' gap='24px' flexGrow={1}>
          <FormControl size='small' sx={{ width: '400px' }}>
            <FormLabel>Subnets</FormLabel>
            <Autocomplete
              multiple
              filterSelectedOptions
              size='small'
              value={subnetNames}
              onChange={(_, newValue) => setSubnetNames(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={subnetNames.length === 0 ? 'Select subnets' : ''}
                  label=''
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              options={subnetOptions}
            />
          </FormControl>
        </Stack>
        <PanelFooter
          primaryButton={{
            text: 'Save Settings',
            onClick: onSaveClick,
            disabled: subnetNames.length === 0,
            'data-testid': 'save-custom-vnet',
          }}
          secondaryButton={{
            text: 'Cancel',
            onClick: onCancel,
            'data-testid': 'cancel-custom-vnet',
          }}
        />
      </Stack>
    </BottomDialog>
  );
};
